import type { BladeDescriptor } from './types';

// Per-blade liveness seam (HUB-275 slice 5). A blade bound to an entity may
// subscribe to live updates; the header shows a status dot. This is the SEAM +
// status chrome with a stub transport — the real per-blade subscription over the
// @hub/api projection stream is HUB-279 (client-side projections).
//
// Status:
//   live         — subscribed, receiving updates
//   reconnecting — transient drop, retrying
//   offline      — no live channel (or nothing to subscribe to)
//
// Stub behaviour: a bound blade (has entityId) reads as `live`; a bindingless
// blade has nothing to subscribe to → `offline`. A blade can be pinned to a
// status for demos/tests/deep-links via `descriptor.params.liveness` (params are
// URL-encoded, HUB-350), mirroring the `forcedBladeState` pattern. HUB-279
// replaces the stub with a real subscription while keeping this signature.

export type LivenessStatus = 'live' | 'reconnecting' | 'offline';

const STATUSES: readonly LivenessStatus[] = ['live', 'reconnecting', 'offline'];

function isLivenessStatus(v: string | undefined): v is LivenessStatus {
  return v !== undefined && (STATUSES as readonly string[]).includes(v);
}

export function useBladeLiveness(descriptor: BladeDescriptor): LivenessStatus {
  const override = descriptor.params?.liveness;
  if (isLivenessStatus(override)) return override;
  return descriptor.entityId ? 'live' : 'offline';
}

/** Human label for a status (title / aria). */
export function livenessLabel(status: LivenessStatus): string {
  switch (status) {
    case 'live':
      return 'Live';
    case 'reconnecting':
      return 'Reconnecting';
    case 'offline':
      return 'Offline';
  }
}
